// exclude-from-index
import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { cloneDeep } from 'lodash';
import { muiGlobalTheme } from '@themes';
import { Label } from '../Label';

const customizeMuiGlobalTheme = () => {
  const newRadioTheme = cloneDeep(muiGlobalTheme);

  newRadioTheme.components = {
    MuiRadio: {
      styleOverrides: { root: { height: 44, marginBottom: 0 } }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: 'flex-start'
        }
      }
    }
  };

  return createTheme(newRadioTheme);
};

function Radios(props) {
  const { activeRadio, isStacked, name, radios, onChange, disabled } = props;

  const radioTheme = useRef(customizeMuiGlobalTheme());

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={radioTheme.current}>
        {radios.map((radio) => (
          <Fragment key={`rad-${radio.value}`}>
            <FormControlLabel
              control={
                <Radio
                  checked={activeRadio === radio.value}
                  color="primary"
                  key={`rad-${radio.value}`}
                  name={name}
                  onChange={onChange}
                  value={radio.value}
                />
              }
              disabled={radio.isDisabled || disabled}
              key={`fcl-${radio.value}`}
              label={
                <Label
                  disabled={disabled}
                  label={radio.label}
                  description={radio.description}
                  toolTip={radio.toolTip}
                />
              }
            />
            {isStacked && <br />}
          </Fragment>
        ))}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

Radios.propTypes = {
  /**
   * value of radio that should be checked on init
   */
  activeRadio: PropTypes.string,
  /**
   * stacks the radios
   */
  isStacked: PropTypes.bool,
  /**
   * identifier to link radios together
   */
  name: PropTypes.string.isRequired,

  /**
   * Callback fired when the value is checked/unchecked
   */
  onChange: PropTypes.func,
  /**
   * isDisabled use of the radio and updates the styles
   * label: Displays text next to the radio
   * value: Value to return when checked
   */
  radios: PropTypes.arrayOf(
    PropTypes.shape({
      isDisabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      /** Optional title that appears above label  */
      description: PropTypes.string,
      /** Optional prop for passing tooltip component  */
      toolTip: PropTypes.node
    })
  ).isRequired,
  disabled: PropTypes.bool
};

Radios.defaultProps = {
  activeRadio: '',
  isStacked: false,
  onChange: PropTypes.func,
  disabled: false
};

export default Radios;
